import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TopNavigation from './TopNavigation';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import { Case } from './types';
import { loadAllCases } from './gameLogic';
import { statsService } from './services/stats';
import tiktokIcon from './TikTok_Icon_Black_Circle.png';
import { updatePageSEO } from './utils/seo';

const Archive: React.FC = () => {
  const { yearMonth } = useParams<{ yearMonth?: string }>();
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  // Update SEO
  useEffect(() => {
    if (yearMonth) {
      const [year, month] = yearMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      updatePageSEO(
        `Dimension Flux Archive - ${monthName}`,
        `Browse and play past Dimension Flux logic puzzles from ${monthName}. Solve inter-dimensional mysteries from the archive.`
      );
    } else {
      updatePageSEO(
        'Dimension Flux Archive - Past Puzzles',
        'Browse and play past Dimension Flux logic puzzles. Access the complete archive of inter-dimensional mystery cases.'
      );
    }
  }, [yearMonth]);

  // Calculate difficulty based on clues and types
  const calculateDifficulty = (verifiers: any[]) => {
    const totalClues = verifiers.length;
    // Exclude incident verifiers (IVI*) from intermediate count - they're used at all difficulty levels
    const intermediateCount = verifiers.filter(v => v.ID?.startsWith('IV') && !v.ID?.startsWith('IVI')).length;
    
    if (totalClues <= 4 && intermediateCount === 0) return 'EASY';
    if (totalClues <= 4 && intermediateCount <= 1) return 'MEDIUM';
    if (totalClues <= 5 && intermediateCount <= 2) return 'MEDIUM';
    if (totalClues <= 5 && intermediateCount <= 3) return 'HARD';
    if (totalClues <= 5 && intermediateCount >= 4) return 'EXPERT';
    return 'EXPERT';
  };

  // Get current month or use URL parameter
  const getCurrentMonth = () => {
    if (yearMonth && /^\d{4}-\d{2}$/.test(yearMonth)) {
      return yearMonth;
    }
    // Default to current month, but if it's the 1st of the month, use previous month
    const now = new Date();
    const useDate = now.getDate() === 1 
      ? new Date(now.getFullYear(), now.getMonth() - 1, 1) // Previous month if 1st
      : now;
    return `${useDate.getFullYear()}-${String(useDate.getMonth() + 1).padStart(2, '0')}`;
  };

  const currentMonth = getCurrentMonth();

  useEffect(() => {
    const loadCases = async () => {
      try {
        const allCases = loadAllCases();
        
        // Filter to only show cases from yesterday or earlier
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(23, 59, 59, 999); // End of yesterday
        
        const filteredCases = allCases.filter(caseData => {
          const caseDate = new Date(caseData.incident.date);
          return caseDate <= yesterday;
        });

        // Get available months from filtered cases
        const months = Array.from(new Set(
          filteredCases.map(caseData => {
            const date = new Date(caseData.incident.date);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          })
        )).sort().reverse(); // Most recent first
        setAvailableMonths(months);

        // Filter cases for current month
        const [year, month] = currentMonth.split('-');
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);
        
        // Validate year and month
        if (isNaN(yearNum) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
          console.error('Invalid month format:', currentMonth);
          setCases([]);
          return;
        }
        
        const monthCases = filteredCases.filter(caseData => {
          const caseDate = new Date(caseData.incident.date);
          return caseDate.getFullYear() === yearNum && 
                 caseDate.getMonth() === monthNum - 1;
        });
        
        // Sort by date in reverse order (newest first)
        const sortedCases = monthCases.sort((a, b) => {
          const dateA = new Date(a.incident.date);
          const dateB = new Date(b.incident.date);
          return dateB.getTime() - dateA.getTime();
        });
        
        // If no cases for this month and we're using a URL parameter, redirect to a month with cases
        if (sortedCases.length === 0 && yearMonth && months.length > 0) {
          navigate(`/archive/${months[0]}`, { replace: true });
          return;
        }
        
        setCases(sortedCases);
      } catch (error) {
        console.error('Error loading cases:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCases();
  }, [currentMonth, navigate, yearMonth]);


  const getCaseUrl = (caseData: Case) => {
    // Create URL with date parameter for the specific case
    // Convert YYYY-MM-DD format to YYYYMMDD format for the main game
    const dateParam = caseData.incident.date.replace(/-/g, '');
    return `/?date=${dateParam}`;
  };

  const formatMonthDisplay = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    }).toUpperCase();
  };


  const handleMonthChange = (newMonth: string) => {
    navigate(`/archive/${newMonth}`);
  };

  const isCaseSolved = (caseData: Case) => {
    const stats = statsService.getStats();
    const singleProposalStats = statsService.getSingleProposalStats();
    // Convert case date to YYYY-MM-DD format to match stats
    const caseDate = caseData.incident.date;
    return stats.solvedDates.includes(caseDate) || singleProposalStats.solvedDates.includes(caseDate);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <TopNavigation />
        <ClueRaiderBanner />
        <div className="max-w-xl mx-auto w-full p-2 sm:p-4">
          <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-6 border-2 border-gray-300 shadow-lg mb-4 sm:mb-6 w-full">
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-800 text-lg">Loading archive...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <TopNavigation />
      <ClueRaiderBanner />

      <div className="max-w-xl mx-auto w-full p-2 sm:p-4">
        {/* Header */}
        <div className="bg-white bg-opacity-95 rounded-lg p-2 sm:p-6 border-2 border-gray-300 shadow-lg mb-3 sm:mb-6 w-full">
          <div className="text-center mb-3 sm:mb-6">
            <h1 className="text-xl sm:text-3xl font-oswald font-bold text-gray-900 mb-2 sm:mb-4">Case Archive</h1>
            <p className="text-base font-lora text-gray-700 italic">Browse and replay previous inter-dimensional cases. Each case presents a unique mystery to solve.</p>
          </div>

          {/* Month Selector */}
          <div className="flex justify-center mb-4">
            <select
              value={currentMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-800 font-oswald font-bold text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {formatMonthDisplay(month)}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* Cases List */}
        {cases.length > 0 ? (
          <div className="space-y-3">
            {cases.map((caseData, index) => (
              <Link
                key={caseData.case_id}
                to={getCaseUrl(caseData)}
                className="block group"
              >
                <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-4 border-2 border-gray-300 shadow-lg hover:shadow-xl hover:border-purple-300 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="flex items-center space-x-2 flex-wrap">
                          <span className="text-xs sm:text-sm font-oswald font-semibold text-purple-800 bg-purple-200 bg-opacity-90 px-2 py-1 rounded">
                            {new Date(caseData.incident.date).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}, {new Date(caseData.incident.date).getDate()}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                              (() => {
                                const difficulty = calculateDifficulty(caseData.verifiers);
                                return difficulty === 'EASY' ? 'bg-green-100 text-green-800' :
                                       difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                       difficulty === 'HARD' ? 'bg-orange-100 text-orange-800' :
                                       'bg-red-100 text-red-800';
                              })()
                            }`}
                          >
                            {calculateDifficulty(caseData.verifiers)}
                          </span>
                          {isCaseSolved(caseData) && (
                            <span className="text-xs font-oswald font-semibold text-green-700 bg-green-200 bg-opacity-90 px-2 py-1 rounded">
                              ✓ SOLVED
                            </span>
                          )}
                        </div>
                        <h3 className="text-sm sm:text-base font-oswald font-bold text-gray-900 group-hover:text-purple-800 transition-colors duration-200 line-clamp-2 flex-1">
                          {caseData.incident.brief_headline || caseData.incident.headline}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 mt-1 line-clamp-2 font-lora">
                        {caseData.incident.brief_description || caseData.incident.description}
                      </p>
                    </div>
                    
                    {/* Play Button */}
                    <div className="ml-3 flex-shrink-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-200 bg-opacity-90 rounded-full flex items-center justify-center group-hover:bg-purple-300 transition-all duration-200">
                        <svg 
                          className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5 text-purple-800" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* No Cases Message */
          <div className="bg-white bg-opacity-95 rounded-lg p-6 border-2 border-gray-300 shadow-lg text-center">
            <div className="text-gray-600">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-oswald font-bold text-gray-800 mb-2">No Cases Available</h3>
              <p className="text-sm text-gray-600 font-lora">
                No cases were found for {formatMonthDisplay(currentMonth)}. Try selecting a different month.
              </p>
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-4 border-2 border-gray-300 shadow-lg mt-4 sm:mt-6 w-full">
          <div className="text-center">
            <p className="text-sm text-gray-700 font-lora">
              {cases.length} case{cases.length !== 1 ? 's' : ''} in {formatMonthDisplay(currentMonth)} • {availableMonths.length} month{availableMonths.length !== 1 ? 's' : ''} available
            </p>
            <p className="text-xs text-gray-600 mt-1 font-lora">
              Click any case to replay and solve the mystery
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="text-center mt-4 mb-2">
          <a href="https://x.com/Dimension_Flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 text-white hover:opacity-80" aria-label="X profile">
            <svg className="w-5 h-5 inline-block align-middle" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2H21L13.5 10.5 22.5 22H15.5L10.5 15.5 4.5 22H2L9.5 13.5 1.5 2H8.5L13.5 8.5 18.244 2Z"/>
            </svg>
          </a>
          <a href="https://www.tiktok.com/@dimension_flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 hover:opacity-80" aria-label="TikTok profile">
            <img src={tiktokIcon} alt="TikTok" className="w-5 h-5 inline-block align-middle rounded-full" />
          </a>
        </div>

        {/* Copyright + AI Disclosure */}
        <div className="text-center mt-2 mb-4 text-sm text-white">
          © N.M.A. Pitman 2025 • Contact: <a href="mailto:hello@dimensionflux.com" className="text-white underline">hello@dimensionflux.com</a>
          <div className="mt-1 text-xs opacity-90"><a href="https://www.vecteezy.com/free-vector/portal">Portal Vectors by Vecteezy</a></div>
        </div>
      </div>
    </div>
  );
};

export default Archive;
